import React from "react";
import { useTranslation } from "react-i18next";
import {
  Bolt,
  TrendingUp,
  Zap,
} from "lucide-react";

export default function EnergySummaryCards({ data = {} }) {
  const { t } = useTranslation();

  const formatScaledEnergy = (rawValue, baseUnit = "kWh") => {
    const prefixes = ["k", "M", "G", "T", "P"];
    const normalized = baseUnit.startsWith("k") ? baseUnit.slice(1) : baseUnit;

    let value = Number(rawValue ?? 0);
    let idx = 0;

    while (Math.abs(value) >= 1000 && idx < prefixes.length - 1) {
      value /= 1000;
      idx += 1;
    }

    return {
      valueText: Number.isFinite(value) ? value.toFixed(3) : "0.000",
      unitText: `${prefixes[idx]}${normalized}`,
    };
  };

  const items = [
    {
      label: t('energies.activeEnergy'),
      importKey: "PT_IMPORT",
      exportKey: "PT_EXPORT",
      unit: "kWh",
      icon: <Bolt size={26} className="text-emerald-600" />,
      gradient: "from-emerald-500 to-emerald-700",
      divide: 1000,
    },
    {
      label: t('energies.reactiveEnergy'),
      importKey: "QT_IMPORT",
      exportKey: "QT_EXPORT",
      unit: "kVArh",
      icon: <Zap size={26} className="text-blue-600" />,
      gradient: "from-blue-500 to-blue-700",
      divide: 1000,
    },
    {
      label: t('energies.apparentEnergy'),
      importKey: "ST_IMPORT",
      exportKey: "ST_EXPORT",
      unit: "kVAh",
      icon: <TrendingUp size={26} className="text-indigo-600" />,
      gradient: "from-indigo-500 to-indigo-700",
      divide: 1000,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
      {items.map((item) => {
        const importScaled = formatScaledEnergy(
          Number(data[item.importKey] ?? 0) / item.divide,
          item.unit
        );
        const exportScaled = formatScaledEnergy(
          Number(data[item.exportKey] ?? 0) / item.divide,
          item.unit
        );


        return (
          <div
            key={item.label}
            className="bg-white p-5 rounded-xl border border-slate-200 shadow-md hover:shadow-lg transition-shadow"
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-gray-100 p-2 rounded-lg">
                {item.icon}
              </div>
              <span className="text-md font-semibold text-slate-700">
                {item.label}
              </span>
            </div>

            {/* Values */}
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="flex flex-col">
                <span className="text-xs text-gray-500">{t('energies.import')}</span>
                <span className="text-xl font-bold text-gray-800">
                  {importScaled.valueText}
                </span>
                <span className="text-xs text-gray-400">{importScaled.unitText}</span>
              </div>

              <div className="flex flex-col">
                <span className="text-xs text-gray-500">{t('energies.export')}</span>
                <span className="text-xl font-bold text-gray-800">
                  {exportScaled.valueText}
                </span>
                <span className="text-xs text-gray-400">{exportScaled.unitText}</span>
              </div>
            </div>

            {/* Gradient Line */}
            <div
              className={`h-1 w-full mt-5 rounded-full bg-gradient-to-r ${item.gradient}`}
            />
          </div>
        );
      })}
    </div>
  );
}
